import { HttpHeaders } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';

// Define response interfaces
interface ApiResponse {
  success: boolean;
  message?: string;
  statusCode?: number;
  data?: {
    user?: any;
    access_token?: string;
    token_type?: string;
    expires_in?: number;
  };
  user?: any;
  access_token?: string;
}

interface ProfileData {
  name: string;
  email: string;
  phone_number?: string;
  company?: string;
  gst?: string;
  external_id?: string;
  user_type?: string;
  customer_region?: string | number;
  team?: string;
  'custom:team'?: string;
  'custom:visibility'?: string;
  'custom:gst'?: string;
  first_name?: string;
  last_name?: string;
  profile_complete?: boolean;
  stats?: {
    totalBriefs?: number;
    completed?: number;
    inProgress?: number;
    thisMonth?: number;
  };
}

@Component({
  selector: 'app-customers',
  templateUrl: './customers.component.html',
  styleUrls: ['./customers.component.scss']
})
export class CustomersComponent implements OnInit {
  userProfile: ProfileData | null = null;
  editMode = false;
  profileForm: FormGroup;
  isLoading = false;
  showPassword = false;
  
  stats = {
    totalBriefs: 24,
    completed: 18,
    inProgress: 4,
    thisMonth: 3
  };

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {
    this.profileForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: [''],
      companyName: ['', Validators.required],
      gstNumber: [''],
      firstName: [''],
      lastName: [''],
      password: ['', [Validators.minLength(8)]]
    });
  }

  ngOnInit(): void {
    this.loadUserProfile();
  }

  loadUserProfile(): void {
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      try {
        this.userProfile = JSON.parse(userInfo);
        console.log(this.userProfile);
        if (this.userProfile) {
          this.populateForm(this.userProfile);
        }
      } catch (error) {
        console.error('Error parsing user info:', error);
        this.fetchProfileFromApi();
      }
    } else {
      this.fetchProfileFromApi();
    }
  }

  fetchProfileFromApi(): void {
    this.isLoading = true;
    this.apiService.get('customer/profile').subscribe({
      next: (response: unknown) => {
        this.isLoading = false;
        const apiResponse = response as ApiResponse;
        if (apiResponse.success && (apiResponse.user || apiResponse.data?.user)) {
          const userData = apiResponse.user || apiResponse.data?.user;
          this.userProfile = userData;
          localStorage.setItem('userInfo', JSON.stringify(this.userProfile));
          this.populateForm(userData);
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error fetching profile:', error);
        this.snackBar.open('Failed to load profile', 'Close', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  // RM WhatsApp number per sales team (used in the ready-to-scope / meet-RM emails).
  private readonly teamWhatsapp: Record<string, string> = {
    salesonlinead: '8448997285',
    salesnorth: '9871954443',
    saleswest: '9911082058',
    salessouth: '9871913105',
  };

  // RM name per sales team (used in the meet-your-RM email).
  private readonly teamRmName: Record<string, string> = {
    salesonlinead: 'Rahul Jaiswal',
    salesnorth: 'Rajesh Chaudhary',
    saleswest: 'Rahul Baisoya',
    salessouth: 'Gaurav Swami',
  };

  // Fire-and-forget "ready to scope projects" email. Failures are logged only.
  private sendReadyToScopeEmail(email: string, name: string, rmWhatsapp: string): void {
    if (!email) return;
    this.apiService.post('send-client-ready-to-scope', {
      mailto: email,
      client_name: name || 'there',
      rm_whatsapp: rmWhatsapp
    }).subscribe({
      next: () => {},
      error: (err: any) => console.error('Failed to send ready-to-scope email:', err)
    });
  }

  // Fire-and-forget "meet your RM" email. Failures are logged only.
  private sendMeetRmEmail(email: string, name: string, rmName: string, rmWhatsapp: string): void {
    if (!email) return;
    this.apiService.post('send-client-meet-rm', {
      mailto: email,
      client_name: name || 'there',
      rm_name: rmName,
      rm_whatsapp: rmWhatsapp
    }).subscribe({
      next: () => {},
      error: (err: any) => console.error('Failed to send meet-RM email:', err)
    });
  }

  onSubmit(): void {
    if (this.profileForm.invalid) {
      this.markFormGroupTouched(this.profileForm);
      this.snackBar.open('Please fill in all required fields correctly', 'Close', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isLoading = true;
    const formData = this.profileForm.value;
    
    // Prepare update payload
    const updateData: any = {
      customerName: formData.name,
      email: formData.email,
      contactNumber: formData.phoneNumber,
      companyName: formData.companyName || '',
      gst: formData.gstNumber || ''
    };

    // Add password only if provided and meets minimum length
    if (formData.password && formData.password.length >= 8) {
      updateData.password = formData.password;
      updateData.password_confirmation = formData.password; // Laravel requires this
    }

    console.log('Sending update data:', { ...updateData, password: updateData.password ? '***' : undefined });

    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      this.isLoading = false;
      this.snackBar.open('Authentication token missing. Please login again.', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    this.apiService.post('customer/update-profile', updateData, headers).subscribe({
      next: (response: unknown) => {
        this.isLoading = false;
        const apiResponse = response as ApiResponse;
        
        if (apiResponse.success) {
          const responseData = apiResponse.data || {};
          const userData = responseData.user || apiResponse.user || {};
          
          const updatedProfile: ProfileData = {
            name: userData.name || formData.name,
            email: userData.email || formData.email,
            company: userData.company || formData.companyName,
            gst: userData.gst || formData.gstNumber,
            phone_number: userData.phone_number || formData.phoneNumber,
            external_id: userData.external_id || this.userProfile?.external_id,
            user_type: userData.user_type || this.userProfile?.user_type,
            customer_region: userData.customer_region || this.userProfile?.customer_region,
            team: userData.team || this.userProfile?.team,
            first_name: userData.first_name || formData.firstName,
            last_name: userData.last_name || formData.lastName,
            profile_complete: userData.profile_complete || true,
            'custom:gst': userData.gst || this.userProfile?.['custom:gst'],
            stats: userData.stats || this.userProfile?.stats,
            'custom:team': userData.team || this.userProfile?.['custom:team'] || this.userProfile?.team,
            'custom:visibility': 'customer'
          };

          this.userProfile = updatedProfile;
          localStorage.setItem('userInfo', JSON.stringify(this.userProfile));

          // Profile completed: send the "ready to scope projects" email, then
          // the "meet your RM" email — both keyed to the customer's sales team.
          if (updatedProfile.email) {
            const teamKey = (updatedProfile.team || '').toLowerCase();
            const rmWhatsapp = this.teamWhatsapp[teamKey] || this.teamWhatsapp['salesonlinead'];
            const rmName = this.teamRmName[teamKey] || this.teamRmName['salesonlinead'];
            this.sendReadyToScopeEmail(updatedProfile.email, updatedProfile.name || 'there', rmWhatsapp);
            this.sendMeetRmEmail(updatedProfile.email, updatedProfile.name || 'there', rmName, rmWhatsapp);
          }

          if (responseData.access_token) {
            localStorage.setItem('accessToken', responseData.access_token);
          }

          this.editMode = false;
          
          // Clear password field after successful update
          this.profileForm.patchValue({
            password: ''
          });
          
          const message = updateData.password ? 'Profile and password updated successfully!' : 'Profile updated successfully!';
          this.snackBar.open(message, 'Close', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
          
          setTimeout(() => {
            this.router.navigate(['workspace/customer/dashboard']);
          }, 2000);
        } else {
          this.snackBar.open(apiResponse.message || 'Update failed', 'Close', {
            duration: 3000,
            panelClass: ['error-snackbar']
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Update error:', error);
        this.handleUpdateError(error);
      }
    });
  }

  private handleUpdateError(error: any): void {
    let errorMessage = 'Failed to update profile';
    
    if (error.error) {
      if (error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error.detail) {
        errorMessage = error.error.detail;
      } else if (typeof error.error === 'string') {
        try {
          const parsedError = JSON.parse(error.error);
          errorMessage = parsedError.message || parsedError.detail || errorMessage;
        } catch (e) {
          errorMessage = error.error;
        }
      }
    }

    this.snackBar.open(errorMessage, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getProfileInitials(): string {
    if (!this.userProfile?.name) return '?';
    const names = this.userProfile.name.split(' ');
    return names.map((n: string) => n[0]).join('').toUpperCase().substring(0, 2);
  }

  getCompanyName(): string {
    return this.userProfile?.company || 'No company';
  }

  getEmail(): string {
    return this.userProfile?.email || 'No email';
  }

  getPhoneNumber(): string {
    return this.userProfile?.phone_number || 'No phone number';
  }

  getGstNumber(): string {
    return this.userProfile?.gst || this.userProfile?.['custom:gst'] || 'Not provided';
  }

  getProfileData(): ProfileData {
    return this.userProfile || {
      name: '',
      email: '',
      company: '',
      phone_number: '',
      gst: ''
    };
  }

  isEmailEditable(): boolean {
    const email = this.getEmail();
    return !email || email === 'No email' || email.trim() === '';
  }

  isPhoneEditable(): boolean {
    const phone = this.getPhoneNumber();
    return !phone || phone === 'No phone number' || phone.trim() === '';
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (!this.editMode && this.userProfile) {
      this.populateForm(this.userProfile);
      // Clear password field when canceling
      this.profileForm.patchValue({
        password: ''
      });
    } else if (this.editMode) {
      this.updateFieldValidators();
    }
  }

  updateFieldValidators(): void {
    const emailControl = this.profileForm.get('email');
    const phoneControl = this.profileForm.get('phoneNumber');
    
    if (emailControl) {
      if (this.isEmailEditable()) {
        emailControl.setValidators([Validators.required, Validators.email]);
      } else {
        emailControl.clearValidators();
      }
      emailControl.updateValueAndValidity();
    }
    
    if (phoneControl) {
      if (this.isPhoneEditable()) {
        phoneControl.setValidators([Validators.required]);
      } else {
        phoneControl.clearValidators();
      }
      phoneControl.updateValueAndValidity();
    }
  }

  populateForm(profile: ProfileData): void {
    const nameParts = profile.name ? profile.name.split(' ') : ['', ''];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    this.profileForm.patchValue({
      name: profile.name || '',
      email: profile.email || '',
      phoneNumber: profile.phone_number || '',
      companyName: profile.company || '',
      gstNumber: profile.gst || profile['custom:gst'] || '',
      firstName: firstName,
      lastName: lastName,
      password: ''
    });

    this.updateFieldValidators();

    if (profile.stats) {
      this.stats = {
        totalBriefs: profile.stats.totalBriefs || this.stats.totalBriefs,
        completed: profile.stats.completed || this.stats.completed,
        inProgress: profile.stats.inProgress || this.stats.inProgress,
        thisMonth: profile.stats.thisMonth || this.stats.thisMonth
      };
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}